import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import MetricCard from './components/MetricCard';
import StatusCard from './components/StatusCard';
import TrendChart from './components/TrendChart';

import AIInsightsPanel from './components/AIInsightsPanel';
import { trackFeatureUsage } from '../../services/analyticsService';
import { exportToPDF, exportExecutiveDashboardToCSV } from '../../services/exportService';
import { getSystemDataContext } from '../../services/dataContextService';

import Select from '../../components/ui/Select';
import WheelChart from './components/WheelChart';


const ExecutiveView = () => {
  const navigate = useNavigate();
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date()?.getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState(Math.ceil((new Date()?.getMonth() + 1) / 3));
  const [lastRefreshed, setLastRefreshed] = useState(new Date());

  useEffect(() => {
    trackFeatureUsage('executive_dashboard_view');
  }, []);

  const getCurrentQuarter = () => {
    const month = new Date()?.getMonth() + 1;
    return Math.ceil(month / 3);
  };

  const formatLastRefreshed = (date) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months?.[date?.getMonth()];
    const day = date?.getDate();
    const year = date?.getFullYear();
    const hours = date?.getHours();
    const minutes = date?.getMinutes()?.toString()?.padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    return `${month} ${day}, ${year} | ${displayHours}:${minutes}${ampm}`;
  };

  const yearOptions = [
    { value: 2024, label: '2024' },
    { value: 2025, label: '2025' },
    { value: 2026, label: '2026' },
  ];

  const quarterOptions = [
    { value: 1, label: 'Q1' },
    { value: 2, label: 'Q2' },
    { value: 3, label: 'Q3' },
    { value: 4, label: 'Q4' },
  ];

  // Mock Data - Business Anchors
  const businessAnchors = {
    totalRevenue: {
      title: "Total Revenue",
      value: "$24.8M",
      trend: "up",
      trendValue: "+12.5%",
      icon: "DollarSign"
    },
    totalCost: {
      title: "Total Cost",
      value: "$18.2M",
      trend: "up",
      trendValue: "+8.3%",
      icon: "TrendingUp"
    },
    grossProfit: {
      title: "Gross Profit",
      value: "$6.6M",
      trend: "up",
      trendValue: "+18.2%",
      icon: "PiggyBank"
    },
    profitMargin: {
      title: "Profit Margin",
      value: "26.6%",
      trend: "up",
      trendValue: "+4.2%",
      icon: "Percent"
    },
    totalSales: {
      title: "Total Sales",
      value: "$28.5M",
      trend: "up",
      trendValue: "+15.8%",
      icon: "ShoppingCart"
    }
  };

  const revenueTrendData = [
    { name: "Jul", value: 3200000, previousYear: 2900000, plan: 3100000 },
    { name: "Aug", value: 3450000, previousYear: 3100000, plan: 3300000 },
    { name: "Sep", value: 3800000, previousYear: 3400000, plan: 3600000 },
    { name: "Oct", value: 4100000, previousYear: 3700000, plan: 3900000 },
    { name: "Nov", value: 4350000, previousYear: 3900000, plan: 4200000 },
    { name: "Dec", value: 4900000, previousYear: 4200000, plan: 4600000 }
  ];

  const costTrendData = [
    { name: "Jul", value: 2400000, previousYear: 2200000, plan: 2300000 },
    { name: "Aug", value: 2600000, previousYear: 2400000, plan: 2500000 },
    { name: "Sep", value: 2850000, previousYear: 2600000, plan: 2700000 },
    { name: "Oct", value: 3100000, previousYear: 2800000, plan: 2900000 },
    { name: "Nov", value: 3250000, previousYear: 3000000, plan: 3100000 },
    { name: "Dec", value: 3600000, previousYear: 3200000, plan: 3400000 }
  ];

  const profitTrendData = [
    { name: "Jul", value: 800000, previousYear: 700000, plan: 800000 },
    { name: "Aug", value: 850000, previousYear: 700000, plan: 800000 },
    { name: "Sep", value: 950000, previousYear: 800000, plan: 900000 },
    { name: "Oct", value: 1000000, previousYear: 900000, plan: 1000000 },
    { name: "Nov", value: 1100000, previousYear: 900000, plan: 1100000 },
    { name: "Dec", value: 1300000, previousYear: 1000000, plan: 1200000 }
  ];

  const salesTrendData = [
    { name: "Jul", value: 3500000, previousYear: 3100000, plan: 3400000 },
    { name: "Aug", value: 3800000, previousYear: 3300000, plan: 3600000 },
    { name: "Sep", value: 4200000, previousYear: 3700000, plan: 4000000 },
    { name: "Oct", value: 4600000, previousYear: 4000000, plan: 4400000 },
    { name: "Nov", value: 4900000, previousYear: 4300000, plan: 4700000 },
    { name: "Dec", value: 5500000, previousYear: 4800000, plan: 5200000 }
  ];

  const planVsActualData = [
    { name: "Q1", Plan: 9500000, Actual: 10450000 },
    { name: "Q2", Plan: 10200000, Actual: 13350000 },
    { name: "Q3", Plan: 11000000, Actual: 0 },
    { name: "Q4", Plan: 12500000, Actual: 0 }
  ];

  // Mock Data - Enterprise Health
  const enterpriseHealth = {
    overallPerformance: {
      title: "Overall Business Performance",
      status: "excellent",
      statusLabel: "Excellent",
      description: "All key metrics trending positively with strong execution",
      icon: "TrendingUp"
    },
    businessHealth: {
      title: "Business Health Status",
      status: "good",
      statusLabel: "Healthy",
      description: "Strong fundamentals with minor areas for improvement",
      icon: "Heart"
    }
  };

  const performanceTrendData = [
    { name: "Week 1", value: 82 },
    { name: "Week 2", value: 85 },
    { name: "Week 3", value: 88 },
    { name: "Week 4", value: 91 },
    { name: "Week 5", value: 89 },
    { name: "Week 6", value: 93 }
  ];

  const performanceForecastData = [
    { name: "Jun", value: 93 },
    { name: "Jul", value: 95 },
    { name: "Aug", value: 96 },
    { name: "Sep", value: 94 },
    { name: "Oct", value: 97 },
    { name: "Nov", value: 98 }
  ];

  // Mock Data - Efficiency & Value
  const efficiencyMetrics = {
    costVsPerformance: {
      title: "Cost vs Performance Ratio",
      value: "1.42",
      trend: "up",
      trendValue: "+0.15",
      icon: "Scale"
    },
    valueLeakage: {
      title: "Money Lost / Inefficiency",
      value: "$1.2M",
      trend: "down",
      trendValue: "-18.5%",
      icon: "AlertTriangle"
    }
  };

  const productivityTrendData = [
    { name: "Jan", value: 78 },
    { name: "Feb", value: 82 },
    { name: "Mar", value: 85 },
    { name: "Apr", value: 88 },
    { name: "May", value: 91 },
    { name: "Jun", value: 94 }
  ];

  // Type of Spend Data
  const typeOfSpendData = [
    { name: "Personnel", value: 8500000 },
    { name: "Operations", value: 4200000 },
    { name: "Technology", value: 2800000 },
    { name: "Marketing", value: 1500000 },
    { name: "R&D", value: 1200000 }
  ];

  // Receivables Data
  const receivablesTrendData = [
    { name: "Jul", value: 5200000, plan: 5000000 },
    { name: "Aug", value: 5450000, plan: 5200000 },
    { name: "Sep", value: 5800000, plan: 5500000 },
    { name: "Oct", value: 6100000, plan: 5800000 },
    { name: "Nov", value: 6350000, plan: 6100000 },
    { name: "Dec", value: 6900000, plan: 6500000 }
  ];

  // Vendor Spend Analysis Data
  const vendorSpendTrendData = [
    { name: "Jul", value: 1800000, plan: 1700000 },
    { name: "Aug", value: 1950000, plan: 1850000 },
    { name: "Sep", value: 2100000, plan: 2000000 },
    { name: "Oct", value: 2300000, plan: 2200000 },
    { name: "Nov", value: 2450000, plan: 2400000 },
    { name: "Dec", value: 2700000, plan: 2600000 }
  ];

  // Mock Data - Risk & Stability
  const riskMetrics = {
    executionRisk: {
      title: "Delivery / Execution Risk",
      status: "warning",
      statusLabel: "Moderate Risk",
      description: "3 projects showing delay indicators, monitoring required",
      icon: "AlertCircle"
    },
    businessStability: {
      title: "Business Stability Indicator",
      status: "good",
      statusLabel: "Stable",
      description: "Consistent performance with low volatility",
      icon: "Shield"
    }
  };

  // Mock Data - AI Insights
  const aiInsights = {
    performance: [
      {
        title: "Execution Health Score",
        score: 87,
        maxScore: 100,
        status: "positive",
        description: "Strong execution across all business units with minimal delays",
        icon: "Activity"
      },
      {
        title: "Execution Variance",
        score: 5.2,
        maxScore: 10,
        status: "positive",
        description: "Low variance indicates consistent delivery performance",
        icon: "BarChart3"
      },
      {
        title: "Execution Risk Score",
        score: 23,
        maxScore: 100,
        status: "positive",
        description: "Low risk profile with proactive mitigation strategies in place",
        icon: "AlertTriangle"
      },
      {
        title: "Performance Degradation Signal",
        score: 12,
        maxScore: 100,
        status: "positive",
        description: "Minimal degradation detected, systems operating optimally",
        icon: "TrendingDown"
      }
    ],
    financial: [
      {
        title: "Cost Leakage Indicator",
        score: 3.8,
        maxScore: 10,
        status: "positive",
        description: "Minimal cost leakage identified, strong financial controls",
        icon: "DollarSign"
      },
      {
        title: "High-Cost Outlier Indicator",
        score: 2,
        maxScore: 10,
        status: "positive",
        description: "Few outliers detected, cost distribution is healthy",
        icon: "AlertCircle"
      },
      {
        title: "Cost Stability Score",
        score: 91,
        maxScore: 100,
        status: "positive",
        description: "Highly stable cost structure with predictable patterns",
        icon: "Shield"
      }
    ],
    workforce: [
      {
        title: "Low Performer Ratio",
        score: 8,
        maxScore: 100,
        status: "positive",
        description: "Low percentage of underperformers, strong team capability",
        icon: "Users"
      },
      {
        title: "Attrition Risk Indicator",
        score: 15,
        maxScore: 100,
        status: "neutral",
        description: "Moderate attrition risk in specific departments, action plans active",
        icon: "UserMinus"
      }
    ],
    meta: [
      {
        title: "Decision Confidence Score",
        score: 89,
        maxScore: 100,
        status: "positive",
        description: "High confidence in data quality and analytical insights for decision-making",
        icon: "Brain"
      }
    ]
  };

  const handleDrillDown = () => {
    navigate('/drill-down-analysis');
  };

  const handleExportPDF = async () => {
    trackFeatureUsage('executive_dashboard_pdf_export');
    const systemData = getSystemDataContext();
    const result = await exportToPDF({
      title: 'Executive Dashboard Report',
      data: systemData,
      type: 'executive-dashboard'
    });
    if (result?.success) {
      console.log('PDF exported successfully:', result?.fileName);
    } else {
      console.error('PDF export failed:', result?.error);
    }
  };

  const handleExportCSV = () => {
    trackFeatureUsage('executive_dashboard_csv_export');
    const systemData = getSystemDataContext();
    const result = exportExecutiveDashboardToCSV(systemData);
    if (result?.success) {
      console.log('CSV exported successfully');
    } else {
      console.error('CSV export failed:', result?.error);
    }
  };

  /* ─── Section header helper ─────────────────────────────────────────── */
  const SectionHeader = ({ icon, iconColor, iconBg, label }) => (
    <div className="flex items-center gap-2 mb-3">
      <div className={`flex items-center justify-center w-7 h-7 rounded-md ${iconBg}`}>
        <Icon name={icon} size={15} color={iconColor} />
      </div>
      <h2 className="text-base font-heading font-semibold text-foreground tracking-wide uppercase">
        {label}
      </h2>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* ── Enterprise shell: fixed top bar + 3-zone canvas ─────────────── */}
      <div className="flex pt-16 min-h-screen">

        {/* ── Zone 1 + 2: Main content (flexible) ─────────────────────────── */}
        <div className="flex-1 min-w-0 overflow-y-auto px-5 py-4">

          {/* ── Command bar: breadcrumb + title + controls in one compact row ── */}
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-4 min-w-0">
              <Breadcrumb
                items={[
                  { label: 'Home', path: '/executive-view' },
                  { label: 'Executive View', path: '/executive-view' },
                ]}
              />
              <div className="hidden md:block h-4 w-px bg-border" />
              <div className="hidden md:block">
                <h1 className="text-xl font-heading font-bold text-foreground leading-tight">
                  Executive View
                </h1>
                <p className="text-xs text-muted-foreground font-body">
                  Integrated business intelligence for leaders
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={handleExportPDF}
                className="p-1.5 rounded-md hover:bg-muted transition-smooth focus-ring"
                title="Export PDF"
              >
                <Icon name="Download" size={16} />
              </button>
              <button
                onClick={handleExportCSV}
                className="p-1.5 rounded-md hover:bg-muted transition-smooth focus-ring"
                title="Export CSV"
              >
                <Icon name="FileText" size={16} />
              </button>
              <span className="text-xs text-muted-foreground hidden lg:block">
                {formatLastRefreshed(lastRefreshed)}
              </span>
            </div>
          </div>

          {/* ── SECTION: Business Anchors ─────────────────────────────────── */}
          <section className="mb-5">
            {/* Section header row with year/quarter selectors */}
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-7 h-7 rounded-md bg-primary/10">
                  <Icon name="Anchor" size={15} color="var(--color-primary)" />
                </div>
                <h2 className="text-base font-heading font-semibold text-foreground tracking-wide uppercase">
                  Business Anchors
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e?.target?.value))}
                  options={yearOptions}
                  className="w-22"
                />
                <Select
                  value={selectedQuarter}
                  onChange={(e) => setSelectedQuarter(Number(e?.target?.value))}
                  options={quarterOptions}
                  className="w-18"
                />
                <span className="text-xs font-medium text-foreground">
                  {selectedYear} Q{selectedQuarter}
                </span>
              </div>
            </div>

            {/* KPI row — 5 tiles in a single row */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
              <MetricCard {...businessAnchors?.totalRevenue} onClick={handleDrillDown} />
              <MetricCard {...businessAnchors?.totalCost} onClick={handleDrillDown} />
              <MetricCard {...businessAnchors?.grossProfit} onClick={handleDrillDown} />
              <MetricCard {...businessAnchors?.profitMargin} onClick={handleDrillDown} />
              <MetricCard {...businessAnchors?.totalSales} onClick={handleDrillDown} />
            </div>

            {/* Revenue + Cost side-by-side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
              <TrendChart
                title="Revenue Trend"
                data={revenueTrendData}
                dataKey="value"
                previousYearData={true}
                planData={true}
                showPlan={selectedYear === new Date()?.getFullYear()}
                type="area"
                color="var(--color-success)"
                onClick={handleDrillDown}
              />
              <TrendChart
                title="Cost Trend"
                data={costTrendData}
                dataKey="value"
                previousYearData={true}
                planData={true}
                showPlan={selectedYear === new Date()?.getFullYear()}
                type="area"
                color="var(--color-error)"
                onClick={handleDrillDown}
              />
            </div>

            {/* Profit + Sales side-by-side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
              <TrendChart
                title="Profit Trend"
                data={profitTrendData}
                dataKey="value"
                previousYearData={true}
                planData={true}
                showPlan={selectedYear === new Date()?.getFullYear()}
                type="line"
                color="var(--color-primary)"
                onClick={handleDrillDown}
              />
              <TrendChart
                title="Sales Trend"
                data={salesTrendData}
                dataKey="value"
                previousYearData={true}
                planData={true}
                showPlan={selectedYear === new Date()?.getFullYear()}
                type="area"
                color="var(--color-accent)"
                onClick={handleDrillDown}
              />
            </div>

            {/* Spend + Receivables + Vendor in one row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <WheelChart
                title="Type of Spend"
                data={typeOfSpendData}
                onClick={handleDrillDown}
              />
              <TrendChart
                title="Receivables Trend"
                data={receivablesTrendData}
                dataKey="value"
                previousYearData={false}
                planData={true}
                showPlan={true}
                type="line"
                color="var(--color-success)"
                onClick={handleDrillDown}
              />
              <TrendChart
                title="Vendor Spend Analysis"
                data={vendorSpendTrendData}
                dataKey="value"
                previousYearData={false}
                planData={true}
                showPlan={true}
                type="line"
                color="var(--color-warning)"
                onClick={handleDrillDown}
              />
            </div>
          </section>

          {/* ── SECTION: Enterprise Health ────────────────────────────────── */}
          <section className="mb-5">
            <SectionHeader
              icon="Heart"
              iconColor="var(--color-success)"
              iconBg="bg-success/10"
              label="Enterprise Health"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <StatusCard {...enterpriseHealth?.overallPerformance} onClick={handleDrillDown} />
              <StatusCard {...enterpriseHealth?.businessHealth} onClick={handleDrillDown} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <TrendChart
                title="Performance Trend"
                data={performanceTrendData}
                dataKey="value"
                previousYearData={false}
                planData={false}
                type="line"
                color="var(--color-success)"
                onClick={handleDrillDown}
              />
              <TrendChart
                title="Performance Forecast"
                data={performanceForecastData}
                dataKey="value"
                previousYearData={false}
                planData={false}
                type="area"
                color="var(--color-secondary)"
                onClick={handleDrillDown}
              />
            </div>
          </section>

          {/* ── SECTION: Efficiency & Value ───────────────────────────────── */}
          <section className="mb-5">
            <SectionHeader
              icon="Zap"
              iconColor="var(--color-accent)"
              iconBg="bg-accent/10"
              label="Efficiency & Value"
            />

            {/* Metric cards + productivity chart in one horizontal band */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <MetricCard {...efficiencyMetrics?.costVsPerformance} onClick={handleDrillDown} />
              <MetricCard {...efficiencyMetrics?.valueLeakage} onClick={handleDrillDown} />
              <TrendChart
                title="Productivity Trend"
                data={productivityTrendData}
                dataKey="value"
                previousYearData={false}
                planData={false}
                type="area"
                color="var(--color-accent)"
                onClick={handleDrillDown}
              />
            </div>
          </section>

          {/* ── SECTION: Risk & Stability ─────────────────────────────────── */}
          <section className="mb-6">
            <SectionHeader
              icon="Shield"
              iconColor="var(--color-warning)"
              iconBg="bg-warning/10"
              label="Risk & Stability"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <StatusCard {...riskMetrics?.executionRisk} onClick={handleDrillDown} />
              <StatusCard {...riskMetrics?.businessStability} onClick={handleDrillDown} />
            </div>
          </section>
        </div>

        {/* ── Zone 3: Right AI Intelligence Rail ───────────────────────────── */}
        <AIInsightsPanel
          insights={aiInsights}
          isOpen={aiPanelOpen}
          onToggle={() => setAiPanelOpen(!aiPanelOpen)}
        />
      </div>
    </div>
  );
};

export default ExecutiveView;